import type { SignupAnswers, SignupPrefill } from "plg/types";
import { hasPlgSession } from "plg/auth/plgTokenStore";

export const PLG_SIGNUP_SESSION_KEY = "plgSignupSession";
export const PLG_EXPIRED_QUERY = "expired";

/**
 * The setup steps' state in this tab. Before the account exists it holds the sign-up token; after the first
 * step creates the account the token is dropped (the PLG session takes over) and `answers` is a local copy of
 * what is saved, so Back and the step routing work without a request per screen.
 */
export interface SignupSession {
  signupToken?: string;
  prefill: SignupPrefill | null;
  enriched: boolean;
  answers: SignupAnswers;
}

function tokenPayload(token: string): Record<string, unknown> | null {
  try {
    const [, body] = token.split(".");
    // UTF-8, so a name such as "José" reads right
    const bytes = Uint8Array.from(atob(body.replace(/-/g, "+").replace(/_/g, "/")), (ch) => ch.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string, nowMs: number = Date.now()): boolean {
  const exp = tokenPayload(token)?.exp;
  return typeof exp !== "number" || exp * 1000 <= nowMs;
}

/** The sign-up token's provider and email (read only for analytics; the server verifies the token). */
export function signupTokenClaims(token: string): { provider?: string; email?: string } {
  const p = tokenPayload(token);
  return {
    provider: typeof p?.provider === "string" ? p.provider : undefined,
    email: typeof p?.email === "string" ? p.email : undefined,
  };
}

/** The name Google / Microsoft gave, to prefill step 1 (`prefill: "sso.firstName"`). Empty for a magic link. */
export function signupTokenName(token: string): { firstName?: string; lastName?: string } {
  const p = tokenPayload(token);
  const text = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : undefined);
  return { firstName: text(p?.firstName), lastName: text(p?.lastName) };
}

export function clearSignupSession(): void {
  try {
    sessionStorage.removeItem(PLG_SIGNUP_SESSION_KEY);
  } catch {
    // storage unavailable — nothing to clear
  }
}

export function saveSignupSession(session: SignupSession): void {
  try {
    sessionStorage.setItem(PLG_SIGNUP_SESSION_KEY, JSON.stringify(session));
  } catch {
    // Private mode: the flow still works within the tab, it just won't survive a refresh.
  }
}

export function loadSignupSession(): SignupSession | null {
  let session: SignupSession | null = null;
  try {
    session = JSON.parse(sessionStorage.getItem(PLG_SIGNUP_SESSION_KEY) || "null");
  } catch {
    return null;
  }
  if (!session) return null;
  // Before the account: the sign-up token must still be valid. After it: only with the PLG session it belongs to.
  if (session.signupToken ? isTokenExpired(session.signupToken) : !hasPlgSession()) {
    clearSignupSession();
    return null;
  }
  return { ...session, answers: session.answers || {} };
}

export function updateSignupAnswers(partial: SignupAnswers): void {
  const session = loadSignupSession();
  if (!session) return;
  saveSignupSession({ ...session, answers: { ...session.answers, ...partial } });
}
